import { defineStore } from "pinia";
import { useUserStore } from "./User";

// Export constants for use in other stores and components
export const EVENT_TYPES = {
    NOTES: "notes",
    CONVERSATION: "conversation",
    INVITE: "invite",
    ASSIGN: "assign",
    REQUEST: "request",
    TIMER: "timer",
    ACTION: "action",
};

export const EVENT_SUB_TYPES = {
    // Document
    NOTES_CREATED: "Created",
    NOTES_CHANGED: "Changed",
    NOTES_DELETED: "Deleted",

    // Conversation
    CONVERSATION_CREATED: "Created",
    CONVERSATION_ARCHIVED: "Archived",
    CONVERSATION_RESTORED: "Restored",

    // Invite
    INVITE_ADDED: "added",
    INVITE_REMOVED: "removed",

    // Assign
    ASSIGN_ASSIGNED: "assigned",
    ASSIGN_ENDED: "assignment ended",
    ASSIGN_UNASSIGNED: "unassigned",

    // Request
    REQUEST_ATTENTION: "attention",
    REQUEST_SUPPORT: "support",
    REQUEST_STATUS: "request_status",
    REQUEST_ATTENTION_CLOSED: "attention_closed",
    REQUEST_SUPPORT_CLOSED: "support_closed",
    REQUEST_STATUS_CLOSED: "request_status_closed",

    // Timer
    TIMER_TASK_STOP: "Task_Stop",
    TIMER_SHIFT_OUT: "Shift_Out",
    TIMER_TASK_START: "Task_Start",
    TIMER_SHIFT_IN: "Shift_In",
    TIMER_TASK_PAUSE: "Task_Pause",
    TIMER_SHIFT_PAUSE: "Shift_Pause",
    TIMER_TASK_RESUME: "Task_Resume",
    TIMER_SHIFT_RESUME: "Shift_Resume",

    // Action
    ACTION_SPACE_MOVE: "space_move",
    ACTION_BOARD_MOVE: "board_move",
    ACTION_ITEM_CATEGORY_MOVE: "item_category_move",
    ACTION_ITEM_MOVE: "item_move",
    ACTION_SPACE_GROUP_NAME_RENAMED: "space_group_name_renamed",
    ACTION_SPACE_NAME_RENAMED: "space_name_renamed",
    ACTION_BOARD_GROUP_NAME_RENAMED: "board_group_name_renamed",
    ACTION_BOARD_NAME_RENAMED: "board_name_renamed",
    ACTION_ITEM_GROUP_NAME_RENAMED: "item_group_name_renamed",
    ACTION_ITEM_TITLE_RENAMED: "item_title_renamed",
    ACTION_TOPIC_TITLE_RENAMED: "topic_title_renamed",
    ACTION_GROUP_TITLE_RENAMED: "group_title_renamed",
    ACTION_ITEM_OPEN: "item_open",
    ACTION_ITEM_CLOSED: "item_closed",
    ACTION_AGENT_RESPONSE: "agent_response",
};

export const ENTITY_TYPES = {
    WORKSPACE: "workspace",
    BOARD: "board",
    ITEM: "item",
    TOPIC: "topic",
    GROUP: "group",
    WORKSPACE_GROUP: "workspace-group",
    BOARD_GROUP: "board-group",
    ITEM_GROUP: "item-group",
    DM: "dm",
    GROUP_ONE_OFF: "group-one-off",
    ITEM_FEEDBACK: "feedback",
};

export const SOURCES = {
    WEB: "web",
    WORKSTATION: "workstation",
    CRON_JOB: "cron-job",
};

export const ICONS = {
    // Document icons
    NOTES_CREATED: "notes",
    NOTES_CHANGED: "notes",
    NOTES_DELETED: "notes",

    // Conversation icons
    TOPIC_CREATED: "topic-created",
    GROUP_CREATED: "group-created",
    ITEM_CREATED: "item-created",
    CONVERSATION_ARCHIVED: "conversation-archived",
    CONVERSATION_RESTORED: "conversation-restored",

    // Invite icons
    ADD_MEMBER: "add-member",
    REMOVE_MEMBER: "remove-member",

    // Assign icons
    ITEM_ASSIGNED: "item-assigned",
    END_ASSIGNMENT: "end-assignment",

    // Request icons
    ATTENTION_REQUEST: "attention-request",
    SUPPORT_REQUEST: "support-request",
    STATUS_REQUEST: "status-request",
    ATTENTION_REQUEST_CLOSED: "attention-request-closed",
    SUPPORT_REQUEST_CLOSED: "support-request-closed",
    STATUS_REQUEST_CLOSED: "status-request-closed",

    // Timer icons
    STOP_FILLED: "stop-filled",
    STOP_STROKE: "stop-stroke",
    PLAY_FILLED: "play-filled",
    PLAY_STROKE: "play-stroke",
    PAUSE_FILLED: "pause-filled",
    PAUSE_STROKE: "pause-stroke",

    STARTED_TIMER_WORKSPACE: "started-timer-workspace",
    STARTED_TIMER_WORKSTATION: "started-timer-workstation",
    PAUSED_TIMER_WORKSPACE: "paused-timer-workspace",
    PAUSED_TIMER_WORKSTATION: "paused-timer-workstation",
    RESUMED_TIMER_WORKSPACE: "resumed-timer-workspace",
    RESUMED_TIMER_WORKSTATION: "resumed-timer-workstation",
    STOPPED_TIMER_WORKSPACE: "stopped-timer-workspace",
    STOPPED_TIMER_WORKSTATION: "stopped-timer-workstation",

    // Action icons
    ITEM_MOVED: "item-moved",
    ITEM_TILE_RENAMED: "item-tile-renamed",
    ITEM_OPENED: "item-opened",
    ITEM_CLOSED: "item-closed",
};

export const REQUEST_TYPES = [
    EVENT_SUB_TYPES.REQUEST_ATTENTION,
    EVENT_SUB_TYPES.REQUEST_SUPPORT,
    EVENT_SUB_TYPES.REQUEST_STATUS,
    EVENT_SUB_TYPES.REQUEST_ATTENTION_CLOSED,
    EVENT_SUB_TYPES.REQUEST_SUPPORT_CLOSED,
    EVENT_SUB_TYPES.REQUEST_STATUS_CLOSED,
];

export const useMessageEventsStore = defineStore("eventConstants", {
    state: () => ({
        // Only reactive state here
    }),

    getters: {
        // Use exported constants instead of duplicating
        EVENT_TYPES: () => EVENT_TYPES,
        EVENT_SUB_TYPES: () => EVENT_SUB_TYPES,
        ENTITY_TYPES: () => ENTITY_TYPES,
        SOURCES: () => SOURCES,
        ICONS: () => ICONS,
        REQUEST_TYPES: () => REQUEST_TYPES,

        ENTITY_LABELS: () => ({
            workspace: "space",
            board: "board",
            item: "item",
            topic: "topic",
            group: "group",
            "workspace-group": "space group",
            "board-group": "board group",
            "item-group": "item group",
        }),

        TYPE_LABELS: () => ({
            is_space_group: "space group",
            is_space: "space",
            is_board_group: "board group",
            is_board: "board",
            is_item_group: "item group",
            is_item: "item",
            is_topic: "topic",
            is_group_one_off: "topic",
            is_group: "group",
        }),

        // Get events structure
        EVENTS: (state) => state.getEvents(),
    },

    actions: {
        // Get events structure
        getEvents(conversation = null) {
            return {
                [this.EVENT_TYPES.NOTES]: {
                    [this.EVENT_SUB_TYPES.NOTES_CREATED]: {
                        icon: this.ICONS.NOTES_CREATED,
                        format: (authorName, entity_type) => {
                            const label =
                                this.ENTITY_LABELS[entity_type] ??
                                "Conversation";
                            return `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>updated the notes</span> of this ${label}</span>`;
                        },
                    },
                    [this.EVENT_SUB_TYPES.NOTES_CHANGED]: {
                        icon: this.ICONS.NOTES_CHANGED,
                        format: (authorName, entity_type) => {
                            const label =
                                this.ENTITY_LABELS[entity_type] ??
                                "Conversation";
                            return `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>updated the notes</span> of this ${label}</span>`;
                        },
                    },
                    [this.EVENT_SUB_TYPES.NOTES_DELETED]: {
                        icon: this.ICONS.NOTES_DELETED,
                        format: (authorName, entity_type) => {
                            const label =
                                this.ENTITY_LABELS[entity_type] ??
                                "Conversation";
                            return `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>updated the notes</span> of this ${label}</span>`;
                        },
                    },
                },
                [this.EVENT_TYPES.CONVERSATION]: {
                    [this.EVENT_SUB_TYPES.CONVERSATION_CREATED]: {
                        icon: (entity_type) =>
                            entity_type === "topic"
                                ? this.ICONS.TOPIC_CREATED
                                : entity_type === "group"
                                  ? this.ICONS.GROUP_CREATED
                                  : this.ICONS.ITEM_CREATED,
                        format: (authorName, entity_type) => {
                            const label =
                                this.ENTITY_LABELS[entity_type] ??
                                "Conversation";
                            return `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>created</span> the ${label}</span>`;
                        },
                    },
                    [this.EVENT_SUB_TYPES.CONVERSATION_ARCHIVED]: {
                        icon: this.ICONS.CONVERSATION_ARCHIVED,
                        format: () =>
                            "<span class='message-event-text'>Conversation Archived</span>",
                    },
                    [this.EVENT_SUB_TYPES.CONVERSATION_RESTORED]: {
                        icon: this.ICONS.CONVERSATION_RESTORED,
                        format: () =>
                            "<span class='message-event-text'>Conversation Restored</span>",
                    },
                },
                [this.EVENT_TYPES.INVITE]: {
                    [this.EVENT_SUB_TYPES.INVITE_ADDED]: {
                        icon: this.ICONS.ADD_MEMBER,
                        format: (authorName, event, typeLabel) => {
                            const valid_user_profiles =
                                this.getValidUserProfiles(
                                    event.user_profiles,
                                    conversation,
                                );
                            if (valid_user_profiles.length === 0) return "";

                            const memberText =
                                valid_user_profiles.length > 1
                                    ? "members"
                                    : "member";
                            return `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>added</span> ${typeLabel} ${memberText} ${this.extractUserList(valid_user_profiles, conversation)}</span>`;
                        },
                    },
                    [this.EVENT_SUB_TYPES.INVITE_REMOVED]: {
                        icon: this.ICONS.REMOVE_MEMBER,
                        format: (authorName, event, typeLabel) => {
                            const valid_user_profiles =
                                this.getValidUserProfiles(
                                    event.user_profiles,
                                    conversation,
                                );
                            if (valid_user_profiles.length === 0) return "";

                            const memberText =
                                valid_user_profiles.length > 1
                                    ? "members"
                                    : "member";
                            return `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>removed</span> ${typeLabel} ${memberText} ${this.extractUserList(valid_user_profiles, conversation)}</span>`;
                        },
                    },
                },
                [this.EVENT_TYPES.ASSIGN]: {
                    [this.EVENT_SUB_TYPES.ASSIGN_ASSIGNED]: {
                        icon: this.ICONS.ITEM_ASSIGNED,
                        format: (authorName, event) =>
                            `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>assigned</span> the item to ${this.extractUserList(event.user_profiles, conversation)}</span>`,
                    },
                    [this.EVENT_SUB_TYPES.ASSIGN_ENDED]: {
                        icon: this.ICONS.END_ASSIGNMENT,
                        format: (authorName, event) =>
                            `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>ended the assignment</span> to ${this.extractUserList(event.user_profiles, conversation)}</span>`,
                    },
                    [this.EVENT_SUB_TYPES.ASSIGN_UNASSIGNED]: {
                        icon: this.ICONS.END_ASSIGNMENT,
                        format: (authorName, event) =>
                            `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>ended the assignment</span> to ${this.extractUserList(event.user_profiles, conversation)}</span>`,
                    },
                },
                [this.EVENT_TYPES.REQUEST]: {
                    [this.EVENT_SUB_TYPES.REQUEST_ATTENTION]: {
                        icon: this.ICONS.ATTENTION_REQUEST,
                        format: (authorName, event) =>
                            `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>requested attention</span> from ${this.extractUserList(event.user_profiles, conversation)}</span>`,
                    },
                    [this.EVENT_SUB_TYPES.REQUEST_SUPPORT]: {
                        icon: this.ICONS.SUPPORT_REQUEST,
                        format: (authorName, event) =>
                            `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>requested support</span> from ${this.extractUserList(event.user_profiles, conversation)}</span>`,
                    },
                    [this.EVENT_SUB_TYPES.REQUEST_STATUS]: {
                        icon: this.ICONS.STATUS_REQUEST,
                        format: (authorName, event) =>
                            `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>requested status</span> from ${this.extractUserList(event.user_profiles, conversation)}</span>`,
                    },
                    [this.EVENT_SUB_TYPES.REQUEST_ATTENTION_CLOSED]: {
                        icon: this.ICONS.ATTENTION_REQUEST_CLOSED,
                        format: (authorName, event) =>
                            `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>closed</span> the attention request${event.receiver ? ` to ${event.receiver}` : ""}</span>`,
                    },
                    [this.EVENT_SUB_TYPES.REQUEST_SUPPORT_CLOSED]: {
                        icon: this.ICONS.SUPPORT_REQUEST_CLOSED,
                        format: (authorName, event) =>
                            `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>closed</span> the support request${event.receiver ? ` to ${event.receiver}` : ""}</span>`,
                    },
                    [this.EVENT_SUB_TYPES.REQUEST_STATUS_CLOSED]: {
                        icon: this.ICONS.STATUS_REQUEST_CLOSED,
                        format: (authorName, event) =>
                            `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>closed</span> the status request${event.receiver ? ` to ${event.receiver}` : ""}</span>`,
                    },
                },
                [this.EVENT_TYPES.TIMER]: {
                    [this.EVENT_SUB_TYPES.TIMER_TASK_STOP]: {
                        icon: (source) =>
                            source === this.SOURCES.WEB
                                ? this.ICONS.STOPPED_TIMER_WORKSPACE
                                : this.ICONS.STOPPED_TIMER_WORKSTATION,
                        format: (authorName, event) =>
                            `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>stopped the task</span> from ${this.getSourceLabel(event.source, event.action_type)}</span>`,
                    },
                    [this.EVENT_SUB_TYPES.TIMER_SHIFT_OUT]: {
                        icon: (source) =>
                            source === this.SOURCES.WEB
                                ? this.ICONS.STOPPED_TIMER_WORKSPACE
                                : this.ICONS.STOPPED_TIMER_WORKSTATION,
                        format: (authorName, event) =>
                            `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>stopped the shift</span> from ${this.getSourceLabel(event.source, event.action_type)}</span>`,
                    },
                    [this.EVENT_SUB_TYPES.TIMER_TASK_START]: {
                        icon: (source) =>
                            source === this.SOURCES.WEB
                                ? this.ICONS.STARTED_TIMER_WORKSPACE
                                : this.ICONS.STARTED_TIMER_WORKSTATION,
                        format: (authorName, event) =>
                            `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>started the task</span> from ${this.getSourceLabel(event.source, event.action_type)}</span>`,
                    },
                    [this.EVENT_SUB_TYPES.TIMER_SHIFT_IN]: {
                        icon: (source) =>
                            source === this.SOURCES.WEB
                                ? this.ICONS.STARTED_TIMER_WORKSPACE
                                : this.ICONS.STARTED_TIMER_WORKSTATION,
                        format: (authorName, event) =>
                            `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>started the shift</span> from ${this.getSourceLabel(event.source, event.action_type)}</span>`,
                    },
                    [this.EVENT_SUB_TYPES.TIMER_TASK_PAUSE]: {
                        icon: (source) =>
                            source === this.SOURCES.WEB
                                ? this.ICONS.PAUSED_TIMER_WORKSPACE
                                : this.ICONS.PAUSED_TIMER_WORKSTATION,
                        format: (authorName, event) =>
                            `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>paused the task</span> from ${this.getSourceLabel(event.source, event.action_type)}</span>`,
                    },
                    [this.EVENT_SUB_TYPES.TIMER_SHIFT_PAUSE]: {
                        icon: (source) =>
                            source === this.SOURCES.WEB
                                ? this.ICONS.PAUSED_TIMER_WORKSPACE
                                : this.ICONS.PAUSED_TIMER_WORKSTATION,
                        format: (authorName, event) =>
                            `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>paused the shift</span> from ${this.getSourceLabel(event.source, event.action_type)}</span>`,
                    },
                    [this.EVENT_SUB_TYPES.TIMER_TASK_RESUME]: {
                        icon: (source) =>
                            source === this.SOURCES.WEB
                                ? this.ICONS.RESUMED_TIMER_WORKSPACE
                                : this.ICONS.RESUMED_TIMER_WORKSTATION,
                        format: (authorName, event) =>
                            `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>resumed the task</span> from ${this.getSourceLabel(event.source, event.action_type)}</span>`,
                    },
                    [this.EVENT_SUB_TYPES.TIMER_SHIFT_RESUME]: {
                        icon: (source) =>
                            source === this.SOURCES.WEB
                                ? this.ICONS.RESUMED_TIMER_WORKSPACE
                                : this.ICONS.RESUMED_TIMER_WORKSTATION,
                        format: (authorName, event) =>
                            `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>resumed the shift</span> from ${this.getSourceLabel(event.source, event.action_type)}</span>`,
                    },
                },
                [this.EVENT_TYPES.ACTION]: {
                    [this.EVENT_SUB_TYPES.ACTION_SPACE_MOVE]: {
                        icon: this.ICONS.ITEM_MOVED,
                        format: (authorName, event) =>
                            `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>moved</span> the space from ${event.old_value} to ${event.new_value}</span>`,
                    },
                    [this.EVENT_SUB_TYPES.ACTION_BOARD_MOVE]: {
                        icon: this.ICONS.ITEM_MOVED,
                        format: (authorName, event) =>
                            `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>moved</span> the board from ${event.old_value} to ${event.new_value}</span>`,
                    },
                    [this.EVENT_SUB_TYPES.ACTION_ITEM_CATEGORY_MOVE]: {
                        icon: this.ICONS.ITEM_MOVED,
                        format: (authorName, event) =>
                            `${authorName} <span class='font-weight-bold'>moved</span> the item group from ${event.old_value} to ${event.new_value}`,
                    },
                    [this.EVENT_SUB_TYPES.ACTION_ITEM_MOVE]: {
                        icon: this.ICONS.ITEM_MOVED,
                        format: (authorName, event) =>
                            `${authorName} <span class='font-weight-bold'>moved</span> the item from ${event.old_value?.name} to ${event.new_value?.name}`,
                    },
                    [this.EVENT_SUB_TYPES.ACTION_SPACE_GROUP_NAME_RENAMED]: {
                        icon: this.ICONS.ITEM_TILE_RENAMED,
                        format: (authorName, event) =>
                            `${authorName} <span class='font-weight-bold'>updated the space group name</span> from ${event.old_value} to ${event.new_value}`,
                    },
                    [this.EVENT_SUB_TYPES.ACTION_SPACE_NAME_RENAMED]: {
                        icon: this.ICONS.ITEM_TILE_RENAMED,
                        format: (authorName, event) =>
                            `${authorName} <span class='font-weight-bold'>updated the space name</span> from ${event.old_value} to ${event.new_value}`,
                    },
                    [this.EVENT_SUB_TYPES.ACTION_BOARD_GROUP_NAME_RENAMED]: {
                        icon: this.ICONS.ITEM_TILE_RENAMED,
                        format: (authorName, event) =>
                            `${authorName} <span class='font-weight-bold'>updated the board group name</span> from ${event.old_value} to ${event.new_value}`,
                    },
                    [this.EVENT_SUB_TYPES.ACTION_BOARD_NAME_RENAMED]: {
                        icon: this.ICONS.ITEM_TILE_RENAMED,
                        format: (authorName, event) =>
                            `${authorName} <span class='font-weight-bold'>updated the board name</span> from ${event.old_value} to ${event.new_value}`,
                    },
                    [this.EVENT_SUB_TYPES.ACTION_ITEM_GROUP_NAME_RENAMED]: {
                        icon: this.ICONS.ITEM_TILE_RENAMED,
                        format: (authorName, event) =>
                            `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>updated the item group name</span> from ${event.old_value} to ${event.new_value}</span>`,
                    },
                    [this.EVENT_SUB_TYPES.ACTION_ITEM_TITLE_RENAMED]: {
                        icon: this.ICONS.ITEM_TILE_RENAMED,
                        format: (authorName, event) =>
                            `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>updated the title</span> from ${event.old_value} to ${event.new_value}</span>`,
                    },
                    [this.EVENT_SUB_TYPES.ACTION_TOPIC_TITLE_RENAMED]: {
                        icon: this.ICONS.ITEM_TILE_RENAMED,
                        format: (authorName, event) =>
                            `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>updated the topic name</span> from ${event.old_value} to ${event.new_value}</span>`,
                    },
                    [this.EVENT_SUB_TYPES.ACTION_GROUP_TITLE_RENAMED]: {
                        icon: this.ICONS.ITEM_TILE_RENAMED,
                        format: (authorName, event) =>
                            `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>updated the group name</span> from ${event.old_value} to ${event.new_value}</span>`,
                    },
                    [this.EVENT_SUB_TYPES.ACTION_ITEM_OPEN]: {
                        icon: this.ICONS.ITEM_OPENED,
                        format: (authorName) =>
                            `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>re-opened</span> the item</span>`,
                    },
                    [this.EVENT_SUB_TYPES.ACTION_ITEM_CLOSED]: {
                        icon: this.ICONS.ITEM_CLOSED,
                        format: (authorName) =>
                            `<span class='message-event-text'>${authorName} <span class='font-weight-bold'>closed</span> the item</span>`,
                    },
                    [this.EVENT_SUB_TYPES.ACTION_AGENT_RESPONSE]: {
                        icon: null,
                        format: (authorName, event) => event?.message || "",
                    },
                },
            };
        },

        // Prepare icon and message for event
        prepareIconAndMessageForEvent(event, authorName, conversation) {
            const type = event?.type;
            const sub_type = event?.sub_type;
            const source = event?.source;
            const entity_type = event?.entity_type;
            const message = event?.message;
            const conversationId = conversation?.id || conversation?.sid;

            const eventConfig = this.getEvents(conversation)[type]
                ? this.getEvents(conversation)[type]
                : this.getEvents(conversation)[this.EVENT_TYPES.REQUEST][type];
            if (!eventConfig) return null;

            // Get type label for invite events
            const type_label = this.getTypeLabelFromConversation(conversation);

            // Handle notes events
            if (type === this.EVENT_TYPES.NOTES) {
                const config = eventConfig[sub_type];
                return {
                    icon: config.icon,
                    message: config.format(authorName, entity_type),
                };
            }

            // Handle conversation events with entity_type logic
            if (
                type === this.EVENT_TYPES.CONVERSATION &&
                sub_type === this.EVENT_SUB_TYPES.CONVERSATION_CREATED
            ) {
                const config = eventConfig[sub_type];
                return {
                    icon: config.icon(entity_type),
                    message: config.format(authorName, event.entity_type),
                };
            }

            // Handle timer events with source logic
            if (type === this.EVENT_TYPES.TIMER && eventConfig[sub_type]) {
                const config = eventConfig[sub_type];
                return {
                    icon: config.icon(source),
                    message: config.format(authorName, event),
                };
            }

            // Handle request events (attention, support, request_status)
            if (this.REQUEST_TYPES.includes(type)) {
                const requestConfig =
                    this.getEvents(conversation)[this.EVENT_TYPES.REQUEST][
                        type
                    ];
                if (requestConfig) {
                    return {
                        icon: requestConfig.icon,
                        message: requestConfig.format(authorName, event),
                    };
                }
            }

            // Handle message-based events (invite, assign)
            if (message && eventConfig[message.toLowerCase()]) {
                const config = eventConfig[message.toLowerCase()];
                return {
                    icon: config.icon,
                    message: config.format(authorName, event, type_label),
                };
            }

            // Handle sub_type based events
            if (sub_type && eventConfig[sub_type]) {
                const config = eventConfig[sub_type];
                return {
                    icon: config.icon,
                    message: config.format(authorName, event),
                };
            }

            return null;
        },

        // Get icon for event (backward compatibility)
        getIconForEvent(event) {
            const config = this.prepareIconAndMessageForEvent(event);
            return config?.icon || null;
        },

        // Get formatted event message (backward compatibility)
        getFormattedEvent(event, authorName, conversation) {
            const config = this.prepareIconAndMessageForEvent(
                event,
                authorName,
                conversation,
            );
            return config?.message || "No event message available";
        },

        // Helper method to get type label from conversation
        getTypeLabelFromConversation(conversation) {
            for (const [key, label] of Object.entries(this.TYPE_LABELS)) {
                if (conversation[key]) {
                    return label;
                }
            }
            return "";
        },

        // Helper method to get entity label
        getEntityLabel(entityType) {
            return this.ENTITY_LABELS[entityType] ?? "Conversation";
        },

        // Helper method to extract user list for formatting
        extractUserList(user_profiles, conversation) {
            const userStore = useUserStore();
            let str = "";
            let index = 0;

            user_profiles.forEach((member) => {
                const user = this.getUserProfileById(member.id, conversation);

                if (user) {
                    let added_str = "";

                    if (
                        index === user_profiles.length - 1 &&
                        user_profiles.length > 1
                    ) {
                        added_str = " and ";
                    } else if (index > 0) {
                        added_str = ",";
                    }

                    str += `${added_str} ${user?.name?.trim() ?? user?.display_name?.trim()}`;
                }

                index++;
            });

            return str;
        },

        // Helper method to get source label
        getSourceLabel(source, action_type) {
            if (source === this.SOURCES.WEB) return "workspace";
            if (
                source === this.SOURCES.WORKSTATION &&
                !action_type &&
                action_type === "manual"
            )
                return "workstation";
            if (
                source === this.SOURCES.CRON_JOB ||
                (source === this.SOURCES.WORKSTATION &&
                    (action_type === "inactive" || action_type === "automatic"))
            )
                return "automation";
            return source;
        },

        // Helper method to get valid user profiles
        getValidUserProfiles(user_profiles, conversation = null) {
            return user_profiles.filter((member) => {
                const user = this.getUserProfileById(member.id, conversation);
                return user?.name?.trim() ?? user?.display_name?.trim();
            });
        },

        getUserProfileById(id, conversation = null) {
            let user;
            if (conversation) {
                user = conversation.getParticipantById(id);
            }

            if (user) {
                return user;
            }

            return useUserStore().getUserProfile(id);
        },

        isMoveOrRenameEvent(sub_type) {
            const allowed_sub_types = [
                this.EVENT_SUB_TYPES.ACTION_SPACE_MOVE,
                this.EVENT_SUB_TYPES.ACTION_BOARD_MOVE,
                this.EVENT_SUB_TYPES.ACTION_ITEM_CATEGORY_MOVE,
                this.EVENT_SUB_TYPES.ACTION_ITEM_MOVE,
                this.EVENT_SUB_TYPES.ACTION_SPACE_NAME_RENAMED,
                this.EVENT_SUB_TYPES.ACTION_SPACE_GROUP_NAME_RENAMED,
                this.EVENT_SUB_TYPES.ACTION_BOARD_NAME_RENAMED,
                this.EVENT_SUB_TYPES.ACTION_BOARD_GROUP_NAME_RENAMED,
                this.EVENT_SUB_TYPES.ACTION_ITEM_GROUP_NAME_RENAMED,
                this.EVENT_SUB_TYPES.ACTION_ITEM_TITLE_RENAMED,
            ];
            return allowed_sub_types.includes(sub_type);
        },

        isMessageEvent(message) {
            return (
                message?.is_invite ||
                message?.is_uninvite ||
                message?.is_assign ||
                message?.is_unassign ||
                message?.is_attention ||
                message?.is_attention_closed ||
                message?.is_request_status ||
                message?.is_request_status_closed ||
                message?.is_support ||
                message?.is_support_closed ||
                message?.is_conversation ||
                message?.is_timer ||
                message?.is_action ||
                message?.is_notes
            );
        },
    },
});
